import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, X } from "lucide-react";
import { Seo } from "../components/Seo";
import { Breadcrumbs, Disclaimer } from "../components/Layout";
import { CommissionExample, CtaLink } from "../components/Blocks";
import { publicApi, inr } from "../lib/api";
import { useT } from "../lib/i18n";
import { useTl } from "../components/Forms";
import NotFound from "./NotFound";

const STYLE = {
  "starter-partner": "border border-slate-200 bg-white",
  "growth-partner": "border-2 border-amber-500 bg-amber-50/40 lg:-translate-y-2",
  "network-partner": "border-2 border-slate-900 bg-slate-900 text-white",
};

export function PlanCards({ plans }) {
  const { t } = useT();
  const tl = useTl();
  return (
    <div className="grid md:grid-cols-3 gap-6 items-stretch" data-testid="plan-cards">
      {plans.map((p) => {
        const dark = p.slug === "network-partner";
        return (
          <div key={p.slug} className={`rounded-2xl p-6 sm:p-8 flex flex-col shadow-sm transition-transform ${STYLE[p.slug]}`} data-testid={`plan-card-${p.slug}`}>
            <p className={`font-mono text-xs uppercase tracking-widest ${dark ? "text-amber-400" : "text-amber-800"}`}>{t("plans.draft")} · v{p.version}</p>
            <h3 className="text-2xl font-bold mt-2">{tl(p.name)}</h3>
            <p className="font-mono text-3xl font-bold mt-3" data-testid={`plan-price-${p.slug}`}>{inr(p.price)}</p>
            <p className={`text-xs mt-1 ${dark ? "text-slate-300" : "text-slate-600"}`}>{tl(p.gst_note)}</p>
            <p className={`mt-4 text-sm ${dark ? "text-slate-200" : "text-slate-700"}`}>{tl(p.summary)}</p>
            <p className="mt-4 text-sm font-semibold">{t("plans.proposed", { rate: (p.commission_rate * 100).toFixed(0) })}</p>
            <ul className="mt-4 space-y-2 text-sm flex-1">{p.inclusions.slice(0, 4).map((i) => <li key={i} className="flex gap-2"><Check className="h-4 w-4 shrink-0 mt-0.5 text-emerald-600" />{tl(i)}</li>)}</ul>
            <Link to={`/partner-plans/${p.slug}`} className={`mt-6 text-center font-semibold py-2.5 rounded-lg ${dark ? "bg-amber-500 text-slate-950 hover:bg-amber-400" : "bg-slate-900 text-white hover:bg-slate-800"}`} data-testid={`plan-details-${p.slug}`}>{t("plans.viewFull")}</Link>
          </div>
        );
      })}
    </div>
  );
}

const ROWS = [["price", "Price", (v) => inr(v)], ["commission_rate", "Proposed commission (of ERCR)", (v) => `${(v * 100).toFixed(0)}%`], ["lead_access", "Lead access & allocation"], ["eligibility", "Eligibility"], ["validity", "Validity"], ["gst_note", "GST"], ["invoice_party", "Invoice details"], ["territory", "Territory"], ["training", "Training"], ["material_delivery", "Material delivery"], ["renewal", "Renewal"], ["shipping_travel", "Shipping & travel"], ["refund", "Cancellation & refund"]];

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const { t } = useT();
  const tl = useTl();
  useEffect(() => { publicApi.get("/public/plans").then((r) => setPlans(r.data)); }, []);
  return (
    <div className="container-x py-12 sm:py-16" data-testid="plans-page">
      <Seo title={t("plans.seoTitle")} description={tl("Compare Starter, Growth and Network partner plans: fees, GST, inclusions, exclusions, training and commission rates.")} crumbs={[{ name: "Partner Plans", to: "/partner-plans" }]} />
      <Breadcrumbs items={[{ name: "Partner Plans", to: "/partner-plans" }]} />
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4" data-testid="page-h1">{t("plans.h1")}</h1>
      <div className="mb-10 max-w-3xl"><Disclaimer>{t("common.draftNote")}</Disclaimer></div>
      <PlanCards plans={plans} />
      <h2 className="h2 mt-16 mb-6">{t("plans.compareTitle")}</h2>
      <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-sm min-w-[720px]" data-testid="plan-compare-table">
          <thead className="bg-slate-100"><tr><th className="text-left p-3 w-48">{t("plans.item")}</th>{plans.map((p) => <th key={p.slug} className="text-left p-3">{tl(p.name)}</th>)}</tr></thead>
          <tbody>{ROWS.map(([k, l, f]) => <tr key={k} className="border-t align-top"><td className="p-3 font-medium">{t(`plans.rows.${k}`, null, l)}</td>{plans.map((p) => <td key={p.slug} className="p-3">{f ? f(p[k]) : tl(p[k])}</td>)}</tr>)}</tbody>
        </table>
      </div>
      <div className="grid lg:grid-cols-2 gap-10 mt-16 items-center">
        <div><h2 className="h2 mb-4">{t("plans.comExample")}</h2><p className="text-slate-700">{t("common.noGuarantee")}</p></div>
        <CommissionExample />
      </div>
    </div>
  );
}

export function PlanDetail() {
  const { slug } = useParams();
  const [plan, setPlan] = useState(undefined);
  const { t, tl } = useT();
  useEffect(() => { publicApi.get("/public/plans").then((r) => setPlan(r.data.find((p) => p.slug === slug) || null)); }, [slug]);
  if (plan === null) return <NotFound />;
  if (!plan) return <div className="container-x py-24" />;
  const crumbs = [{ name: "Partner Plans", to: "/partner-plans" }, { name: plan.name, to: `/partner-plans/${slug}` }];
  return (
    <div className="container-x py-12 sm:py-16 max-w-4xl" data-testid="plan-detail-page">
      <Seo title={tl(`${plan.name} Plan`)} description={`${tl(plan.summary)} ${tl("Inclusions, exclusions, training, delivery and refund terms.")}`} crumbs={crumbs} />
      <Breadcrumbs items={crumbs} />
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" data-testid="page-h1">{tl(`${plan.name} Plan`)}</h1>
      <p className="font-mono text-3xl font-bold mt-4">{inr(plan.price)} <span className="text-sm font-normal text-slate-600">({tl(plan.gst_note)})</span></p>
      <p className="text-slate-700 mt-4">{tl(plan.summary)}</p>
      <div className="my-8"><Disclaimer>{t("common.draftNote")}</Disclaimer></div>
      <div className="grid md:grid-cols-2 gap-8">
        <div><h2 className="text-xl font-bold mb-4">{tl("Included")}</h2><ul className="space-y-2">{plan.inclusions.map((i) => <li key={i} className="flex gap-2 text-slate-700"><Check className="h-4 w-4 mt-1 text-emerald-600 shrink-0" />{tl(i)}</li>)}</ul></div>
        <div><h2 className="text-xl font-bold mb-4">{tl("Not included")}</h2><ul className="space-y-2">{plan.exclusions.map((i) => <li key={i} className="flex gap-2 text-slate-700"><X className="h-4 w-4 mt-1 text-red-600 shrink-0" />{tl(i)}</li>)}</ul></div>
      </div>
      <h2 className="text-xl font-bold mt-10 mb-4">{tl("Terms at a glance")}</h2>
      <dl className="grid sm:grid-cols-2 gap-4 text-sm">{ROWS.slice(1).map(([k, l, f]) => <div key={k} className="bg-white border rounded-xl p-4"><dt className="font-semibold">{t(`plans.rows.${k}`, null, l)}</dt><dd className="text-slate-700 mt-1">{f ? f(plan[k]) : tl(plan[k])}</dd></div>)}</dl>
      <div className="mt-10 flex flex-wrap gap-4"><CtaLink to={`/apply?tier=${slug}`} testid="plan-apply-btn">{tl("Apply for this plan")}</CtaLink><Link to="/commission-payout-policy" className="font-semibold underline text-amber-800 self-center" data-testid="plan-commission-policy-link">{tl("Commission policy")}</Link></div>
    </div>
  );
}
